import { RefObject, useEffect, useLayoutEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '../motion';
import {
    canMoveFurther, closingHeight, isScrollArea, overflows, rowOf, scrollPaneOf, settledHeight, wheelPixels,
} from './scrollPane';

/**
 * How long an opened card keeps itself at the top. Its own body mounts a
 * render after the click (CollapsePanel), so until then there may be nothing
 * below it to scroll into; as the list grows, it is scrolled to again. The
 * reader scrolling ends it sooner.
 */
const ALIGN_MS = 1000;

/**
 * The longest the scroll waits for a card closing above this one to leave —
 * past the exit fallback in motion.ts, so it has always gone by then.
 */
const SETTLE_MS = 500;

/**
 * A panel above the card that is still on its way out (usePresence holds it
 * mounted, at full height, through its exit). When it goes, everything under
 * it moves up by its height — so a scroll aimed at the card before then
 * overshoots by that much and has to come back down.
 */
const closingAbove = (card: HTMLElement) => {
    const list = card.parentElement;
    if (!list) return false;
    const top = card.getBoundingClientRect().top;
    return Array.from(list.querySelectorAll('[data-state="closing"]'))
        .some((panel) => !card.contains(panel) && panel.getBoundingClientRect().top < top);
};

/** what an animation changes when it moves a box */
const MOVING = ['transform', 'translate', 'top', 'marginTop'];

/**
 * Animations moving the card itself, or anything between it and the pane: a
 * row that has just mounted rises into place (nx-rise-in), and a scroll aimed
 * at it before it has arrived stops short by however far it still had to go.
 * Its own content animating does not move it, so only the card and its
 * ancestors count — and not the pane's, which carry the pane along with it.
 */
const movingAnimations = (card: HTMLElement, pane: HTMLElement | null): Animation[] => {
    const moving: Animation[] = [];
    for (let el: HTMLElement | null = card; el && el !== pane; el = el.parentElement) {
        if (typeof el.getAnimations !== 'function') break;
        el.getAnimations().forEach((animation) => {
            const effect = animation.effect as KeyframeEffect | null;
            // an animation that never ends (a spinner) is not one to wait for
            const { endTime } = effect?.getComputedTiming() || {};
            // nor one that only recolours it — a shadow or a border on opening
            const moves = effect?.getKeyframes().some((frame) => MOVING.some((property) => property in frame));
            if (animation.playState === 'running' && Number.isFinite(endTime) && moves) moving.push(animation);
        });
    }
    return moving;
};

const READER_TAKES_OVER = ['wheel', 'touchstart', 'keydown'];

/** a wheel this soon after the rows last moved is still moving them */
const SWIPE_MS = 200;

/**
 * Until when each pane's own scrolling is the card being brought to the top
 * (see useAlignOnOpen): Infinity while it is, and a moment after, for the
 * smooth scroll to finish. Not a swipe of the rows.
 */
const aligning = new WeakMap<HTMLElement, number>();

/**
 * A wheel over the open card scrolls what is in it and never the rows behind
 * it: the scroll area under the cursor takes it while it can move that way,
 * and otherwise it goes nowhere. That area also keeps its scroll to itself
 * (`data-contain-scroll`, OpenCard.css): Chrome lets a page cancel only the
 * first event of a scroll, and the rest of a hard spin would otherwise spill
 * on to the rows once the area is at its end.
 *
 * One exception: a swipe that is scrolling the rows when the card comes under
 * the cursor goes on scrolling them — the browser would not carry it on over
 * the card by itself.
 *
 * And while the cursor is moved about inside the card, the rows are held still
 * outright. A free-spinning wheel — Logitech's, flicked hard — keeps scrolling
 * whatever it started on for as long as it spins, past anything the page can
 * cancel; moving into the card mid-spin would otherwise leave the rows going.
 * A card slid under a cursor that has not moved is not held for, so a swipe
 * still carries on through it.
 */
const holdWheel = (card: HTMLElement) => {
    const pane = scrollPaneOf(card);
    let paneMovedAt = -Infinity;
    const onPaneScroll = () => {
        if (pane && Date.now() < (aligning.get(pane) ?? 0)) return;
        paneMovedAt = Date.now();
    };
    const contained = new Set<HTMLElement>();

    let held = false;
    const hold = (on: boolean) => {
        if (!pane || held === on) return;
        held = on;
        // hidden scrolls from code still; the gutter keeps the scrollbar's room
        pane.style.overflowY = on ? 'hidden' : '';
        pane.style.scrollbarGutter = on ? 'stable' : '';
        if (on) paneMovedAt = -Infinity;
    };
    // a real move of the cursor — not the card sliding under one standing still
    const onPointerMove = (event: PointerEvent) => { if (event.movementX || event.movementY) hold(true); };
    const onPointerLeave = () => hold(false);

    const onWheel = (event: WheelEvent) => {
        let area: HTMLElement | null = null;
        for (let el = event.target as HTMLElement | null; el && el !== card; el = el.parentElement) {
            if (isScrollArea(el)) area = el;
        }
        if (area && !contained.has(area)) {
            contained.add(area);
            area.setAttribute('data-contain-scroll', '');
        }
        if (!event.cancelable || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
        if (pane && Date.now() - paneMovedAt < SWIPE_MS) {
            event.preventDefault();
            pane.scrollTop += wheelPixels(event, pane);
            return;
        }
        const down = event.deltaY > 0;
        for (let el = event.target as Element | null; el && el !== card; el = el.parentElement) {
            if (overflows(el) && canMoveFurther(el, down)) return;
        }
        event.preventDefault();
    };

    pane?.addEventListener('scroll', onPaneScroll, { passive: true });
    card.addEventListener('wheel', onWheel, { passive: false });
    card.addEventListener('pointermove', onPointerMove, { passive: true });
    card.addEventListener('pointerleave', onPointerLeave);
    return () => {
        pane?.removeEventListener('scroll', onPaneScroll);
        card.removeEventListener('wheel', onWheel);
        card.removeEventListener('pointermove', onPointerMove);
        card.removeEventListener('pointerleave', onPointerLeave);
        contained.forEach((el) => el.removeAttribute('data-contain-scroll'));
        hold(false);
    };
};

/**
 * Brings a card to the top of the pane it scrolls in: when it is opened, and
 * when `deepLinked` says a link asked for it. Only the scroll moves — the list
 * keeps its order. The card's `scroll-margin-top` is the gap it stops short by.
 *
 * Re-aims whenever the card's list resizes, until the page has settled or the
 * reader scrolls for themselves.
 *
 * While it is open a wheel over it scrolls only what is in it, the card's
 * body, and not the page; a wheel anywhere else scrolls the page, the open card
 * with it. Closing it leaves the page where it is — the closed card where it
 * sits, the rows below coming up under.
 *
 * `revealPrevious` keeps the row before it in view above it, so the reader can
 * see where in the list they are. How far that row starts above this one is
 * put on the card as `--reveal-prev-h`, for its scroll-margin-top to add —
 * before anything aligns or measures the card, which is why this is a layout
 * effect and the hook has to be called before usePaneFitHeight. A number
 * instead of `true` keeps only that share of the row in view — 0.5, its lower
 * half — for rows too tall to give a whole one to.
 *
 * `rowSelector` names the list item the card sits in, where that is not the
 * card itself (see rowOf): it is the row that is aligned, measured and kept.
 */
const useAlignOnOpen = (
    ref: RefObject<HTMLElement>,
    open: boolean,
    deepLinked?: boolean,
    { revealPrevious = false, rowSelector }: { revealPrevious?: boolean | number; rowSelector?: string } = {},
) => {
    const reducedMotion = usePrefersReducedMotion();
    const wasOpen = useRef(open);

    useLayoutEffect(() => {
        const card = ref.current && rowOf(ref.current, rowSelector);
        if (!revealPrevious || !open || !card) return undefined;
        const previous = card.previousElementSibling as HTMLElement | null;
        // from the previous row's top to this one's: its height and whatever
        // the list puts between rows, gap or margin or padding alike — as it
        // will be once a row opened a moment ago (opening this one closed it)
        // has finished closing
        const span = previous
            ? card.getBoundingClientRect().top - previous.getBoundingClientRect().top - closingHeight(previous)
            : 0;
        // A share keeps the lower part of it: the gap, and that much of the
        // row. The card's own scroll-margin shows some of that row already
        // (read before this sets anything), so it counts towards the share —
        // on a page with no sticky header over the list, which is where a
        // share is used.
        const share = revealPrevious === true ? 1 : Number(revealPrevious);
        const base = share < 1 ? parseFloat(window.getComputedStyle(card).scrollMarginTop) || 0 : 0;
        const hidden = previous ? settledHeight(previous) * (1 - share) + base : 0;
        card.style.setProperty('--reveal-prev-h', `${Math.max(0, span - hidden)}px`);
        return () => { card.style.removeProperty('--reveal-prev-h'); };
    }, [open, revealPrevious, ref, rowSelector]);

    // held for as long as it is open, however it came to be
    useEffect(() => {
        const card = ref.current && rowOf(ref.current, rowSelector);
        return open && card ? holdWheel(card) : undefined;
    }, [open, ref, rowSelector]);

    useEffect(() => {
        const opened = open && !wasOpen.current;
        wasOpen.current = open;
        const card = ref.current && rowOf(ref.current, rowSelector);
        if (!card || !(opened || deepLinked)) return undefined;

        const behavior: ScrollBehavior = reducedMotion ? 'auto' : 'smooth';
        const list = card.parentElement;
        const pane = scrollPaneOf(card);
        const align = () => card.scrollIntoView({ behavior, block: 'start' });

        // the list growing is room appearing below the card: aim again — the
        // card has not moved, so this only ever carries on in the same direction
        const observer = list && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(align) : null;
        let waiting: MutationObserver | null = null;
        let started = false;
        let timer: number | undefined;
        let settleTimer: number | undefined;

        // the reader taking the scroll back is the end of it, whatever is left —
        // wherever they do it: a wheel over the nav scrolls the rows too
        const stop = () => {
            // its smooth scroll runs on a moment after
            if (pane && aligning.get(pane) === Infinity) aligning.set(pane, Date.now() + 500);
            started = true;
            waiting?.disconnect();
            observer?.disconnect();
            window.clearTimeout(timer);
            window.clearTimeout(settleTimer);
            READER_TAKES_OVER.forEach((type) => window.removeEventListener(type, stop));
        };

        const start = () => {
            if (started) return;
            started = true;
            waiting?.disconnect();
            window.clearTimeout(settleTimer);
            if (pane) aligning.set(pane, Infinity);
            align();
            if (list) observer?.observe(list);
            timer = window.setTimeout(stop, ALIGN_MS);
        };

        // One scroll, straight to where the card will be: if a card above is
        // still closing, wait for it to go first rather than aim at a place
        // that is about to move.
        const begin = () => {
            if (started || waiting) return;
            window.clearTimeout(settleTimer);
            if (list && closingAbove(card)) {
                waiting = new MutationObserver(() => { if (!closingAbove(card)) start(); });
                waiting.observe(list, { childList: true, subtree: true });
                settleTimer = window.setTimeout(start, SETTLE_MS);
            } else {
                start();
            }
        };

        READER_TAKES_OVER.forEach((type) => window.addEventListener(type, stop, { passive: true }));
        // Nor while the card itself is still on its way in — opened by a link,
        // it mounts open, and rises into place with the rest of the list.
        // SETTLE_MS outlasts any entrance, for one that never ends: paused, or
        // in a tab nobody is looking at.
        const moving = movingAnimations(card, pane);
        if (moving.length) {
            Promise.all(moving.map((animation) => animation.finished.catch(() => undefined))).then(begin);
            settleTimer = window.setTimeout(begin, SETTLE_MS);
        } else {
            begin();
        }
        return stop;
    }, [open, deepLinked, reducedMotion, ref, rowSelector]);
};

export default useAlignOnOpen;
